/**
 * Judging System — 3-judge panel for lift verification.
 *
 * Flow:
 *   1. Lifter submits attempt with video
 *   2. 3 judges assigned (or community queue)
 *   3. Each judge votes white (good) or red (no lift) with reason codes
 *   4. 2/3 majority = good lift, 1/3 or 0/3 = no lift
 *   5. Contested lifts can be escalated to head judge
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('JUDGING');

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export type VoteType = 'white' | 'red';

export type ReasonCode =
  | 'depth'              // squat didn't reach parallel
  | 'lockout'            // incomplete lockout at top
  | 'rack_command'       // didn't wait for rack command
  | 'press_command'      // didn't wait for press command
  | 'down_command'       // didn't wait for down command
  | 'hitching'           // hitching on deadlift
  | 'ramping'            // bar ramping on bench
  | 'soft_knees'         // knees not locked on deadlift
  | 'butt_off_bench'     // glutes left the bench
  | 'downward_movement'  // bar moved downward during press
  | 'belt_support'       // belt touching bench
  | 'heaving'            // excessive body movement
  | 'incomplete_rep';    // rep not completed

export interface LiftAttempt {
  id: string;
  profile_id: string;
  lift_key: string;
  attempt_number: number;
  weight_kg: number;
  video_url: string | null;
  status: string;
  white_lights: number;
  red_lights: number;
}

export interface JudgeVote {
  judge_id: string;
  vote: VoteType;
  reason_codes: ReasonCode[];
  notes?: string;
}

// ────────────────────────────────────────────
// Attempt Management
// ────────────────────────────────────────────

/** Submit a lift attempt for judging */
export async function submitAttempt(
  profileId: string,
  liftKey: string,
  attemptNumber: number,
  weightKg: number,
  videoUrl: string,
  tournamentMatchId?: string,
  seasonId?: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('lift_attempts')
    .insert({
      profile_id: profileId,
      lift_key: liftKey,
      attempt_number: attemptNumber,
      weight_kg: weightKg,
      video_url: videoUrl,
      tournament_match_id: tournamentMatchId,
      season_id: seasonId,
      status: 'submitted',
    })
    .select('id')
    .single();

  if (error) {
    log.error('submitAttempt failed:', error);
    return null;
  }

  // Add to video review queue
  await supabase
    .from('video_review_queue')
    .insert({
      lift_attempt_id: data.id,
      profile_id: profileId,
      video_url: videoUrl,
      priority: tournamentMatchId ? 'high' : 'normal',
      reason: 'ranked_lift',
    });

  return data.id;
}

// ────────────────────────────────────────────
// Voting
// ────────────────────────────────────────────

/** Cast a judge's vote on a lift attempt */
export async function castVote(
  liftAttemptId: string,
  judgeProfileId: string,
  vote: VoteType,
  reasonCodes: ReasonCode[] = [],
  notes?: string,
): Promise<boolean> {
  // Get judge record
  const { data: judge } = await supabase
    .from('judges')
    .select('id')
    .eq('profile_id', judgeProfileId)
    .single();

  if (!judge) {
    log.error('Not a registered judge:', judgeProfileId);
    return false;
  }

  const { error } = await supabase
    .from('judge_votes')
    .upsert({
      lift_attempt_id: liftAttemptId,
      judge_id: judge.id,
      vote,
      reason_codes: reasonCodes,
      notes,
    }, { onConflict: 'lift_attempt_id,judge_id' });

  if (error) {
    log.error('castVote failed:', error);
    return false;
  }

  // Check if all 3 judges have voted
  await checkAndResolveAttempt(liftAttemptId);
  return true;
}

/** Check if enough votes are in and resolve the attempt */
async function checkAndResolveAttempt(liftAttemptId: string): Promise<void> {
  const { data: votes } = await supabase
    .from('judge_votes')
    .select('vote, judge_id')
    .eq('lift_attempt_id', liftAttemptId);

  if (!votes || votes.length < 3) return; // Need 3 votes

  const whiteCount = votes.filter(v => v.vote === 'white').length;
  const redCount = votes.filter(v => v.vote === 'red').length;
  const isGoodLift = whiteCount >= 2;

  await supabase
    .from('lift_attempts')
    .update({
      status: isGoodLift ? 'good_lift' : 'no_lift',
      white_lights: whiteCount,
      red_lights: redCount,
      judged_at: new Date().toISOString(),
    })
    .eq('id', liftAttemptId);

  // Update judge stats
  for (const vote of votes) {
    const agreedWithMajority =
      (isGoodLift && vote.vote === 'white') ||
      (!isGoodLift && vote.vote === 'red');

    // Track judge accuracy — >80% consensus required to retain certification (rulebook)
    if (vote.judge_id) {
      try {
        // Fetch current judge stats
        const { data: judgeData } = await supabase
          .from('judges')
          .select('total_judgments, accurate_judgments')
          .eq('profile_id', vote.judge_id)
          .maybeSingle();

        if (judgeData) {
          const total = (judgeData.total_judgments ?? 0) + 1;
          const accurate = (judgeData.accurate_judgments ?? 0) + (agreedWithMajority ? 1 : 0);
          const accuracy = accurate / total;

          await supabase.from('judges').update({
            total_judgments: total,
            accurate_judgments: accurate,
            accuracy_pct: Math.round(accuracy * 100),
            // Decertify if below 80% threshold after at least 10 judgments
            ...(total >= 10 && accuracy < 0.80
              ? { certification_level: 'suspended' }
              : {}),
          }).eq('profile_id', vote.judge_id);
        }
      } catch { /* non-blocking — don't fail the vote */ }
    }
  }

  // Update video review queue
  await supabase
    .from('video_review_queue')
    .update({
      status: 'approved',
      reviewed_at: new Date().toISOString(),
    })
    .eq('lift_attempt_id', liftAttemptId);

  log.info('Attempt resolved', {
    liftAttemptId,
    result: isGoodLift ? 'good_lift' : 'no_lift',
    whiteCount,
    redCount,
  });
}

/** Contest an attempt result (triggers head judge review) */
export async function contestAttempt(
  liftAttemptId: string,
  profileId: string,
): Promise<boolean> {
  const { data: attempt } = await supabase
    .from('lift_attempts')
    .select('profile_id, status')
    .eq('id', liftAttemptId)
    .single();

  if (!attempt || attempt.profile_id !== profileId) return false;
  if (!['good_lift', 'no_lift'].includes(attempt.status)) return false;

  await supabase
    .from('lift_attempts')
    .update({ status: 'contested' })
    .eq('id', liftAttemptId);

  // Escalate in review queue
  await supabase
    .from('video_review_queue')
    .update({ status: 'escalated', priority: 'urgent' })
    .eq('lift_attempt_id', liftAttemptId);

  return true;
}

// ────────────────────────────────────────────
// Judge Management
// ────────────────────────────────────────────

/** Register as a community judge */
export async function registerAsJudge(profileId: string): Promise<boolean> {
  const { error } = await supabase
    .from('judges')
    .upsert({
      profile_id: profileId,
      certification_level: 'community',
    }, { onConflict: 'profile_id', ignoreDuplicates: true });

  if (error) {
    log.error('registerAsJudge failed:', error);
    return false;
  }
  return true;
}

/** Get pending attempts for a judge to review */
export async function getPendingReviews(limit = 10): Promise<any[]> {
  const { data } = await supabase
    .from('video_review_queue')
    .select(`
      *,
      lift_attempts(*, profiles:profile_id(handle, display_name))
    `)
    .eq('status', 'pending')
    .order('priority', { ascending: false })
    .order('created_at')
    .limit(limit);

  return data ?? [];
}
